import { AppsService as Apps } from '@/common/apps/apps.service';
import { MetadataTypeId, MetadataValue, MetadataValueType } from '@/types/analytics/analytics.types';
import {
    BaseDeviceProps,
    ExpandedDeviceProps,
    FnProps,
    TargetPlatform,
    TargetEntity,
} from '@/types/analytics/device-props.types';
import { i18n_t as $t } from '@/utils/i18n';
import { getActiveApplicationsMap } from '@/data/session/session-globals';

export const DEVICE_OPTIONS = ['unknown', 'Desktop', 'Mobile', 'Tablet', 'Console', 'Wearable', 'Television', 'Bot'];

export const BROWSER_NAME_OPTIONS = [
    'unknown',
    'Chrome',
    'MSIE',
    'Edge',
    'Safari',
    'Firefox',
    'Android',
    'Opera',
    'Blackberry',
    'UCBrowser',
    'Silk',
    'Nokia',
    'NetFront',
    'QQ',
    'Maxthon',
    'SogouExplorer',
    'Spotify',
    'Webkit',
    'Nintendo',
    'Samsung',
    'Yandex',
    'CocCoc',
    'Bot',
    'AppleBot',
    'BaiduBot',
    'BingBot',
    'DuckDuckGoBot',
    'FacebookBot',
    'GoogleBot',
    'LinkedInBot',
    'MsnBot',
    'PingdomBot',
    'TwitterBot',
    'YandexBot',
    'CocCocBot',
    'YahooBot',
];

export const OPERATING_SYSTEM_OPTIONS = [
    'unknown',
    'Windows Phone',
    'Windows',
    'Mac OS X',
    'iOS',
    'Android',
    'Blackberry',
    'ChromeOS',
    'Kindle',
    'WebOS',
    'GNU/Linux',
    'Playstation',
    'Xbox',
    'Nintendo',
    'Bot',
];

const deviceProps = (platform: Array<string>): Array<BaseDeviceProps> => [
    {
        get DisplayName () {
            switch (platform.length) {
                case 1:
                    return platform[0] === 'mobile' ? $t('Device Model') : $t('Browser Name');
                default:
                    return $t('Browser Name / Device Model');
            }
        },
        field: 'name',
        platform: ['mobile', 'web'],
        get values () {
            if (platform.filter((name) => name === 'web').length) {
                return BROWSER_NAME_OPTIONS;
            }

            return undefined;
        },
        order: 1,
    },
    {
        get DisplayName () {
            switch (platform.length) {
                case 1:
                    return platform[0] === 'mobile' ? $t('OS Version') : $t('Browser Version');
                default:
                    return $t('Browser / OS Version');
            }
        },
        field: 'version',
        platform: ['mobile', 'web'],
        order: 2,
    },
    {
        DisplayName: $t('Device Type'),
        field: 'deviceType',
        platform: ['mobile', 'web'],
        values: DEVICE_OPTIONS,
        order: 3,
    },
    {
        DisplayName: $t('Operating System'),
        field: 'os',
        platform: ['mobile', 'web'],
        values: OPERATING_SYSTEM_OPTIONS,
        order: 4,
    },
    {
        DisplayName: $t('App Version'),
        field: 'appVersion',
        platform: ['mobile'],
        order: 5,
    },
    {
        DisplayName: $t('SDK Version'),
        field: 'sdkVersion',
        platform: ['mobile'],
        order: 6,
    },
];

export const DEVICE_PROP_FIELDS: Array<string> = deviceProps([]).map((device) => device.field);

export function deviceValuesByField (params: FnProps): Record<string, string[]> {
    return getBaseDevicePropsArr(params).reduce((acc, { field, values }) => {
        if (values) {
            acc[field] = values;
        }

        return acc;
    }, {} as Record<string, string[]>);
}

export const getAppIdsFromTargets = (targets: Array<{ id: string; appId?: string }>): Array<string> => {
    const appIds = targets.reduce((appIds, target) => {
        if (target?.appId || target?.id) {
            // Targets can be either an event with an appId or an app in the case of "Any Activity"
            appIds.add(target?.appId || target?.id);
        }

        return appIds;
    }, new Set<string>());

    return Array.from(appIds);
};

export const getPlatformFromAppIds = (appIds: Array<string>): Array<TargetPlatform> => {
    const res: Array<TargetPlatform> = [];

    let hasWebApp = false;
    let hasMobileApp = false;

    const activeAppsMap = getActiveApplicationsMap();
    const apps = appIds.map((appId) => {
        return activeAppsMap[appId];
    });

    apps.find((app) => {
        if (Apps.isMobile(app)) {
            hasMobileApp = true;
        } else {
            hasWebApp = true;
        }

        return hasWebApp && hasMobileApp;
    });

    if (hasWebApp) res.push('web');
    if (hasMobileApp) res.push('mobile');

    return res;
};

export const getPlatformFromTargets = (targets: Array<TargetEntity>): Array<TargetPlatform> => {
    return getPlatformFromAppIds(getAppIdsFromTargets(targets));
};

function getBaseDevicePropsArr ({ platform = [], appId, targets }: FnProps): Array<BaseDeviceProps> {
    if (appId) {
        const activeAppsMap = getActiveApplicationsMap();
        const app = activeAppsMap[appId];

        platform.push(Apps.isMobile(app) ? 'mobile' : 'web');
    }

    if (targets?.length) {
        platform = platform.concat(getPlatformFromTargets(targets));
    }

    return deviceProps(platform).filter((prop) => prop.platform.some((type) => platform.includes(type)));
}

export default function getDevicePropsArr (params: FnProps): Array<ExpandedDeviceProps> {
    return getBaseDevicePropsArr(params)
        .sort((a, b) => a.order - b.order)
        .map((result) => ({
            ...result,
            sortable: true,
            kind: MetadataTypeId.Automatic as MetadataTypeId.Automatic,
            label: result.DisplayName,
            prop: `parsedUserAgent.${result.field}`,
            Type: 'string' as MetadataValueType,
            ElementType: 'string' as MetadataValueType,
            isAutomatic: true,
        }));
}

export function devicePropToMetadataValue (dp: ExpandedDeviceProps): MetadataValue {
    return {
        ...dp,
        ElementType: 'string',
        ElementFormat: '',
        Dirty: false,
        isHidden: false,
        isDeleted: false,
        isCalculated: false,
        isPerApp: false,
        isPromoted: false,
        neverIndex: false,
        group: '',
    };
}
